import API from '@/APIs/API';
import AthenaAPI from '@/APIs/AthenaAPI';
import {TextBox} from '@/Core/UI';
import RootStore from '@/Core/mobx/RootStore';
import {
    applyApplicationDataValues,
    applyHubspotValues,
    interpolateParameterTokens,
} from '@/Core/utils/interpolateContent';
import {AssistContentItem} from '@/APIs/types';
import {Box, CircularProgress, InputAdornment, LinearProgress, TextField, Typography} from '@material-ui/core';
import {ExpandMore, Search, ThumbDown, ThumbDownOutlined, ThumbUp, ThumbUpOutlined} from '@material-ui/icons';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import Markdown from 'markdown-to-jsx';
import {observer} from 'mobx-react-lite';
import {useSession} from 'next-auth/react';
import React, {useEffect, useRef, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {useStyles} from './Panel.styles';
import {withStyles} from '@material-ui/core/styles';
import {LoadingSpinner} from '@/components/ui/loading-spinner';
import {TabMappingValue} from '@/CaseManager/FormModal';
import DOMPurify from 'dompurify';

interface PanelProps {
    title?: string;
    subtitle?: string;
    className?: string;
    tab?: TabMappingValue | 'prosper' | 'live-call';
    onValueChangedNotes?: (value: string) => void;
    isLiveCall?: boolean;
    nestPostAppChecklist?: React.ReactNode;
    nestQuote?: React.ReactNode;
    nestPreappChecklist?: React.ReactNode;
    nestOnBottom?: React.ReactNode;
    hideFoldButton?: boolean;
    doOnExpand?: () => void;
    doOnCollapse?: () => void;
}

interface FeedbackPanelProps {
    item: {
        responseId: string;
        wasHelpful: boolean | null;
    };
    isInitialResponse?: boolean;
}

const Accordion = withStyles({
    root: {
        boxShadow: 'none',
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
        '&$expanded': {
            margin: 'auto',
        },
        marginBottom: '20px',
    },
    expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
    root: {
        width: '100%',
        border: '1px solid #d1dbd9',
        borderRadius: '8px',
        minHeight: 56,
        color: '#456d69',
        '& .MuiTypography-root': {
            fontFamily: 'LoraRegular',
            fontSize: '22.54px',
            lineHeight: '120%',
            fontWeight: 700,
        },
        '& .MuiSvgIcon-root': {
            fontSize: '1.7rem',
            color: '#174943',
            transform: 'rotate(270deg)',
        },
        '&$expanded': {
            borderRadius: '8px 8px 0 0',
            minHeight: 56,
            backgroundColor: '#f8fffa',
            '& .MuiSvgIcon-root': {
                transform: 'rotate(180deg)',
            },
        },
    },
    content: {
        '&$expanded': {
            margin: '12px 0',
        },
    },
    expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
    root: {
        width: '100%',
        border: '1px solid #d1dbd9',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        fontFamily: 'FredokaRegular',
        fontSize: '16px',
        lineHeight: '150%',
        color: '#3a645f',
        marginBottom: '20px',
        flexDirection: 'column',
        padding: theme.spacing(2),
        '& .MuiOutlinedInput-multiline': {
            padding: 0,
        },
        '& .MuiOutlinedInput-inputMultiline': {
            fontFamily: 'FredokaRegular',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '150%',
            color: '#456d69',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
        '& .accordionDetailsList': {
            fontFamily: 'FredokaRegular',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '150%',
            color: '#456d69',
            borderRadius: '4px',
            '& ul': {
                listStyleType: 'disc',
                paddingLeft: '24px',
            },
        },
        '& .MuiTypography-root': {
            fontFamily: 'FredokaRegular',
            fontWeight: 400,
            fontSize: '16.9px',
            color: '#456d69',
        },
        '&.accordionDetailsSubAccordion': {
            fontFamily: 'FredokaRegular',
            padding: 0,
            '& .MuiAccordion-root': {
                fontFamily: 'FredokaRegular',
                marginBottom: 0,
                width: '100%',
            },
            '& .MuiAccordionSummary-root': {
                fontFamily: 'FredokaRegular',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: 'none',
                borderRadius: 0,
            },
            '& .MuiAccordionDetails-root': {
                borderRadius: 0,
                border: 'none',
                paddingTop: 0,
                marginBottom: 0,
                backgroundColor: '#f8fffa',
            },
            '& .MuiAccordion-root:first-child .MuiAccordionSummary-root': {
                borderTop: 'none',
            },
            '& .MuiAccordion-root:last-child': {
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
            },
        },
    },
}))(MuiAccordionDetails);

const HTML_TAG_PATTERN = /<[a-z][\s\S]*>/i;

function renderAssistContent(content: string, htmlClassName?: string) {
    if (HTML_TAG_PATTERN.test(content)) {
        return <Typography className={htmlClassName} dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(content)}} />;
    }
    return <Typography style={{whiteSpace: 'pre-wrap'}}>{content}</Typography>;
}

const AgentAssistPanel: React.FC<PanelProps> = observer(
    ({
        className,
        tab,
        onValueChangedNotes,
        isLiveCall = false,
        nestPostAppChecklist,
        nestQuote,
        nestPreappChecklist,
        nestOnBottom,
        hideFoldButton = false,
        doOnExpand,
        doOnCollapse,
    }) => {
        const session = useSession();

        // Determine if we want to allow this
        const shouldAllowResume = true;
        const classes = useStyles(shouldAllowResume);
        const markdownOverrides = {
            hr: {
                props: {
                    className: classes.markdownFormatting,
                },
            },
            h3: {
                props: {
                    className: classes.markdownFormattingExtendedH3,
                },
            },
            ul: {
                props: {
                    className: classes.markdownFormattingExtended,
                },
            },
            ol: {
                props: {
                    className: classes.markdownFormattingExtended,
                },
            },
            li: {
                props: {
                    className: classes.markdownFormattingExtended,
                },
            },
            p: {
                props: {
                    className: classes.markdownFormattingExtended,
                },
            },
        };
        const {assistStore, agentPortalStore, scriptAssistStore} = RootStore;
        const {parameterStore} = agentPortalStore;
        const {
            sendQuery,
            isSendingQuery,
            queryHistory,
            sessionId,
            isCreatingSession,
            initialResponse,
            startAssist,
            resetSession,
            notes,
        } = assistStore;

        const {activeScript, activeFaq, activeTip} = scriptAssistStore;
        const hubspotContactId = agentPortalStore.contactsFormStore.hs_object_id || undefined;
        const hubspotDealId = parameterStore.dealId || undefined;

        // Pre-resolve HubSpot tokens asynchronously; keyed by content id so stale results are ignored.
        // ParameterStore tokens are still resolved synchronously in the render path below.
        const [hubspotCache, setHubspotCache] = useState<Record<string, Record<string, string>>>({});
        const [hubspotDealCache, setHubspotDealCache] = useState<Record<string, Record<string, string>>>({});

        function fetchHubspotTokens(
            objectType: 'contact' | 'deal',
            objectId: string,
            cache: Record<string, Record<string, string>>,
            setCache: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>,
        ) {
            const tokenPrefix = `hubspot_${objectType}_`;
            const items = [activeScript, activeFaq, activeTip].filter(Boolean);
            items.forEach((item) => {
                const cacheKey = `${objectId}:${item?.id}`;
                if (!item || cache[cacheKey]) return;
                const allTokens: string[] = [];
                const TOKEN_RE = /\{\{([^}]+)\}\}/g;
                let m;
                while ((m = TOKEN_RE.exec(item.content)) !== null) {
                    const key = m[1].trim();
                    if (!key.startsWith(tokenPrefix)) continue;
                    // The token may contain a null-coalescing expression, e.g.:
                    //   hubspot_contact_lead_source ?? hubspot_contact_channel
                    // Split on `??` and collect each side as a separate property.
                    const candidates = key.includes('??') ? key.split('??') : [key];
                    for (const candidate of candidates) {
                        const trimmed = candidate.trim();
                        const prop = trimmed.startsWith(tokenPrefix) ? trimmed.slice(tokenPrefix.length) : trimmed;
                        if (prop && !allTokens.includes(prop)) allTokens.push(prop);
                    }
                }
                if (allTokens.length === 0) return;
                AthenaAPI.getHubspotProperties(objectType, objectId, allTokens)
                    .then((data) => {
                        if (!data) return;
                        setCache((prev) => ({...prev, [cacheKey]: data.properties}));
                    })
                    .catch((err) => {
                        console.error('[AgentAssistPanel] Failed to fetch HubSpot contact properties', err);
                    });
            });
        }

        useEffect(() => {
            if (!hubspotContactId) return;
            fetchHubspotTokens('contact', hubspotContactId, hubspotCache, setHubspotCache);
        }, [activeScript?.id, activeFaq?.id, activeTip?.id, hubspotContactId]);

        useEffect(() => {
            if (!hubspotDealId) return;
            fetchHubspotTokens('deal', hubspotDealId, hubspotDealCache, setHubspotDealCache);
        }, [activeScript?.id, activeFaq?.id, activeTip?.id, hubspotDealId]);

        useEffect(() => {
            scriptAssistStore.setTab(tab ?? null);
        }, [tab, scriptAssistStore.tabSyncVersion]);

        // Track single-step prequal section headers crossing the top 1/3 of the viewport.
        // Uses a scroll listener (capture phase) so it fires regardless of which container scrolls.
        // Multi-step prequal uses prequalCurrentStep in the store instead.
        useEffect(() => {
            if (tab !== 'prequal' || parameterStore.instantIulMultiQuestion === true) return;

            function evaluate() {
                const threshold = window.innerHeight / 3;
                const financesEl = document.getElementById('prequal-finances-header');
                if (financesEl) {
                    scriptAssistStore.setScrolledPastFinances(financesEl.getBoundingClientRect().top < threshold);
                }
                const eligibilityEl = document.getElementById('prequal-eligibility-header');
                if (eligibilityEl) {
                    scriptAssistStore.setScrolledPastEligibility(eligibilityEl.getBoundingClientRect().top < threshold);
                }
            }

            window.addEventListener('scroll', evaluate, {passive: true, capture: true});
            evaluate();

            return () => window.removeEventListener('scroll', evaluate, {capture: true});
        }, [tab, parameterStore.instantIulMultiQuestion]);

        const [queryText, setQueryText] = useState<string>('');
        const chatHistoryRef = useRef<HTMLDivElement>(null);
        const lastQueryRef = useRef<HTMLDivElement>(null);
        const [isPostAppChecklistExpanded, setIsPostAppChecklistExpanded] = useState<boolean | undefined>();
        const [isPreappChecklistExpanded, setIsPreappChecklistExpanded] = useState<boolean | undefined>();
        const [isQuotesExpanded, setIsQuotesExpanded] = useState<boolean | undefined>();
        const [isCaseNotesExpanded, setIsCaseNotesExpanded] = useState<boolean | undefined>();
        const [isScriptAssistExpanded, setIsScriptAssistExpanded] = useState<boolean | undefined>();
        const [isFAQExpanded, setIsFAQExpanded] = useState<boolean | undefined>();
        const [isTipsExpanded, setIsTipsExpanded] = useState<boolean | undefined>();
        const [isCopilotActive, setIsCopilotActive] = useState(false);
        const [isCopilotSearchExpanded, setIsCopilotSearchExpanded] = useState(false);
        const [isAiAssistPanelExpanded, setIsAiAssistPanelExpanded] = useState(true);

        const logStartAiAssistMutation = useMutation({
            mutationFn: (agent: string) => API.LogToMixPanel('agent-start-ai-assist-click', {agent}),
        });

        const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setQueryText(event.target.value);
        };

        const handleKeyPress = async (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && queryText.trim() && !isCopilotActive && !isSendingQuery) {
                setIsCopilotActive(true);
                setIsCopilotSearchExpanded(true);
                try {
                    if (!sessionId) {
                        if (session.data?.user?.email) logStartAiAssistMutation.mutate(session.data.user.email);
                        await startAssist({shouldAllowResume, isLiveCall});
                    }
                    await sendQuery(queryText);
                    setQueryText('');
                } finally {
                    setIsCopilotActive(false);
                }
            }
        };
        const assistChatHistoryActive = sessionId && queryHistory?.length > 0;

        useEffect(
            () => lastQueryRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'}),
            [queryHistory?.length],
        );
        useEffect(() => {
            if (isCopilotSearchExpanded) lastQueryRef.current?.scrollIntoView({behavior: 'instant', block: 'nearest'});
        }, [isCopilotSearchExpanded]);

        const interpolateItem = (item: AssistContentItem): string => {
            const steps: Array<(s: string) => string> = [
                (s) => interpolateParameterTokens(s, parameterStore, scriptAssistStore.calcFocusData ?? undefined),
                (s) => applyHubspotValues('contact', s, hubspotCache[`${hubspotContactId}:${item.id}`] ?? {}),
                (s) => applyHubspotValues('deal', s, hubspotDealCache[`${hubspotDealId}:${item.id}`] ?? {}),
                (s) => applyApplicationDataValues(s, scriptAssistStore.applicationData ?? []),
            ];
            return steps.reduce((content, fn) => fn(content), item.content);
        };

        return !isAiAssistPanelExpanded && !hideFoldButton ? (
            <Box className={classes.contentCardMinimized}>
                {nestPostAppChecklist && (
                    <button
                        title="Post-app Checklist"
                        type="button"
                        onClick={() => {
                            doOnExpand?.();
                            setIsAiAssistPanelExpanded(true);
                            setIsPostAppChecklistExpanded(true);
                        }}
                    >
                        <img src="/img/agentassistpanel_preappchecklist.svg" alt="Post-app Checklist" />
                    </button>
                )}
                {nestPreappChecklist && (
                    <button
                        title="Pre-app Checklist"
                        type="button"
                        onClick={() => {
                            doOnExpand?.();
                            setIsAiAssistPanelExpanded(true);
                            setIsPreappChecklistExpanded(true);
                        }}
                    >
                        <img src="/img/agentassistpanel_preappchecklist.svg" alt="Pre-app Checklist" />
                    </button>
                )}
                {nestQuote && (
                    <button
                        title="Quotes"
                        type="button"
                        onClick={() => {
                            doOnExpand?.();
                            setIsAiAssistPanelExpanded(true);
                            setIsQuotesExpanded(true);
                        }}
                    >
                        <img src="/img/agentassistpanel_quotes.svg" alt="Quotes" />
                    </button>
                )}
                <button
                    title="Case Notes"
                    type="button"
                    onClick={() => {
                        doOnExpand?.();
                        setIsAiAssistPanelExpanded(true);
                        setIsCaseNotesExpanded(true);
                    }}
                >
                    <img src="/img/agentassistpanel_addnotes.svg" alt="Case Notes" />
                </button>
                <button
                    title="Script Assist"
                    type="button"
                    onClick={() => {
                        doOnExpand?.();
                        setIsAiAssistPanelExpanded(true);
                        setIsScriptAssistExpanded(true);
                    }}
                >
                    <img src="/img/agentassistpanel_scriptassist.svg" alt="Script Assist" />
                </button>
                <button
                    title="FAQ"
                    type="button"
                    onClick={() => {
                        doOnExpand?.();
                        setIsAiAssistPanelExpanded(true);
                        setIsFAQExpanded(true);
                    }}
                >
                    <img src="/img/agentassistpanel_faq.svg" alt="FAQ" />
                </button>
                <button
                    title="Ask Your Copilot"
                    type="button"
                    onClick={() => {
                        doOnExpand?.();
                        setIsAiAssistPanelExpanded(true);
                    }}
                >
                    <img src="/img/agentassistpanel_askcopilot.svg" alt="Ask Your Copilot" />
                </button>
                <button
                    id="expand-ai-assist-panel-button"
                    title="Expand Ai Assist Panel"
                    type="button"
                    onClick={() => {
                        doOnExpand?.();
                        setIsAiAssistPanelExpanded(true);
                    }}
                >
                    <img src="/img/arrow.svg" alt="Expand Ai Assist Panel" />
                </button>
            </Box>
        ) : (
            <Box className={`${classes.contentCard} ${className || ''}`}>
                {nestPostAppChecklist && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        defaultExpanded={true}
                        expanded={isPostAppChecklistExpanded}
                        onChange={(event, isExpanded) => setIsPostAppChecklistExpanded(isExpanded)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls={'postapp-checklist-content'}
                            id={'postapp-checklist-header'}
                        >
                            <Typography>Post-app Checklist</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{nestPostAppChecklist}</AccordionDetails>
                    </Accordion>
                )}
                {nestPreappChecklist && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        defaultExpanded={true}
                        expanded={isPreappChecklistExpanded}
                        onChange={(event, isExpanded) => setIsPreappChecklistExpanded(isExpanded)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls={'preapp-checklist-content'}
                            id={'preapp-checklist-header'}
                        >
                            <Typography>Pre-app Checklist</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{nestPreappChecklist}</AccordionDetails>
                    </Accordion>
                )}
                {nestQuote && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        defaultExpanded={true}
                        expanded={isQuotesExpanded}
                        onChange={(event, isExpanded) => setIsQuotesExpanded(isExpanded)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls={'quotes-content'}
                            id={'quotes-header'}
                        >
                            <Typography>Quotes</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{nestQuote}</AccordionDetails>
                    </Accordion>
                )}
                <Accordion
                    TransitionProps={{unmountOnExit: true}}
                    expanded={isCaseNotesExpanded}
                    onChange={(event, isExpanded) => setIsCaseNotesExpanded(isExpanded)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls={'case-notes-content'}
                        id={'case-notes-header'}
                    >
                        <Typography>Case Notes</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TextBox
                            fullWidth
                            id={'notes'}
                            aria-label="Notes"
                            placeholder="Notes"
                            value={parameterStore.approval.notes.value || notes}
                            multiline
                            rows={10}
                            onChange={(value) => onValueChangedNotes?.(value)}
                        />
                    </AccordionDetails>
                </Accordion>
                {activeScript && activeScript.content && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        expanded={isScriptAssistExpanded}
                        onChange={(e, isExpanded) => setIsScriptAssistExpanded(isExpanded)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls={'script-assist-content'}
                            id={'script-assist-header'}
                        >
                            <Typography>Script Assist</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {activeScript
                                ? renderAssistContent(interpolateItem(activeScript), classes.htmlContent)
                                : 'Content coming soon.'}
                        </AccordionDetails>
                    </Accordion>
                )}
                {activeFaq && activeFaq.content && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        expanded={isFAQExpanded}
                        onChange={(e, isExpanded) => setIsFAQExpanded(isExpanded)}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />} aria-controls={'faq-content'} id={'faq-header'}>
                            <Typography>FAQ</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {activeFaq
                                ? renderAssistContent(interpolateItem(activeFaq), classes.htmlContent)
                                : 'Content coming soon.'}
                        </AccordionDetails>
                    </Accordion>
                )}
                {activeTip && activeTip.content && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        expanded={isTipsExpanded}
                        onChange={(e, isExpanded) => setIsTipsExpanded(isExpanded)}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />} aria-controls={'tips-content'} id={'tips-header'}>
                            <Typography>Tips</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {activeTip
                                ? renderAssistContent(interpolateItem(activeTip), classes.htmlContent)
                                : 'Content coming soon.'}
                        </AccordionDetails>
                    </Accordion>
                )}
                {sessionId && (
                    <Accordion
                        TransitionProps={{unmountOnExit: true}}
                        expanded={isCopilotSearchExpanded}
                        onChange={(event, isExpanded) => setIsCopilotSearchExpanded(isExpanded)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls={'copilot-search-content'}
                            id={'copilot-search-header'}
                        >
                            <Typography>Copilot Search</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {assistChatHistoryActive && (
                                <div ref={chatHistoryRef} id="assist-chat-history" className={classes.chatContainer}>
                                    {queryHistory.map((item, index) => (
                                        <div
                                            key={item.responseId}
                                            ref={index === queryHistory.length - 1 ? lastQueryRef : null}
                                        >
                                            <div className={classes.userMessageBubble}>
                                                <Typography variant="body2" className={classes.chatHistory}>
                                                    <Markdown options={{overrides: markdownOverrides}}>
                                                        {item.query}
                                                    </Markdown>
                                                </Typography>
                                            </div>
                                            <div className={classes.aiMessageBubble}>
                                                <Typography variant="body2" className={classes.chatHistory}>
                                                    <Markdown options={{overrides: markdownOverrides}}>
                                                        {item.response}
                                                    </Markdown>
                                                </Typography>
                                                <FeedbackPanel item={item} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {(isLiveCall || (!isLiveCall && !sessionId)) && (
                                <Typography variant="h6" className={classes.sectionTitle}>
                                    AI Assist
                                </Typography>
                            )}
                            {initialResponse.response ? (
                                isLiveCall && (
                                    <div className={classes.prePromptContainer}>
                                        <Typography variant="body2" className={classes.initialResponse}>
                                            <Markdown options={{overrides: markdownOverrides}}>
                                                {initialResponse.response}
                                            </Markdown>
                                        </Typography>
                                    </div>
                                )
                            ) : isCreatingSession ? (
                                <div style={{display: 'flex', justifyContent: 'center', padding: '20px'}}>
                                    <CircularProgress style={{color: '#456D69'}} />
                                </div>
                            ) : (
                                <Typography variant="body2" className={classes.sectionDescriptionInactive}>
                                    <span style={{fontWeight: 'bold'}}>Disclaimer:</span>
                                    <br />
                                    <span style={{fontWeight: 'bold', fontSize: 12}}>
                                        This tool provides information for internal purposes only and has not been
                                        reviewed for accuracy. Please use your judgment and verify the information
                                        before relying on it to advise a client.
                                    </span>
                                    <span style={{fontWeight: 'bold', fontSize: 12}}>
                                        {' '}
                                        Do not enter or share any sensitive or personally identifiable information,
                                        including names, emails, phone numbers, Social Security numbers, driver's
                                        license numbers, banking details, health or prescription details, or any other
                                        sensitive information or data that may be used to identify the client. Failure
                                        to safeguard our clients' personal or sensitive data may result in disciplinary
                                        action, up to and including termination of employment. By clicking "Start AI
                                        Assist" below I agree to the above and will comply.
                                    </span>
                                </Typography>
                            )}
                            {initialResponse.responseId && isLiveCall && (
                                <div className={classes.initialResponseFeedbackContainer}>
                                    <FeedbackPanel item={initialResponse} isInitialResponse={true} />
                                </div>
                            )}
                            {(isCopilotActive || isSendingQuery) && (
                                <div
                                    style={{
                                        display: 'flex',
                                        width: '100%',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <LinearProgress className={classes.linearProgressRedBar} variant="query" />
                                </div>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )}
                {/* {shouldAllowResume && (
                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '8px'}}>
                    <Button
                        variant="text"
                        color="secondary"
                        className={classes.resetButton}
                        onClick={() => resetSession(false, isLiveCall)}
                        disabled={!sessionId}
                    >
                        <Refresh />
                    </Button>
                </div>
            )} */}
                <TextField
                    id="ask-your-copilot"
                    name="ask-your-copilot"
                    disabled={isCopilotActive || isSendingQuery}
                    fullWidth
                    variant="outlined"
                    className={classes.queryInput}
                    placeholder="Ask Your Copilot"
                    value={queryText}
                    onChange={handleQueryChange}
                    onKeyDown={handleKeyPress}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {isCopilotActive || isSendingQuery ? (
                                    <LoadingSpinner className="w-5 h-5 mr-1" />
                                ) : (
                                    <Search />
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
                {nestOnBottom}
                {!hideFoldButton && (
                    <button
                        id="fold-ai-assist-panel-button"
                        type="button"
                        title="Fold Ai Assist Panel"
                        onClick={() => {
                            doOnCollapse?.();
                            setIsAiAssistPanelExpanded(false);
                            setIsPostAppChecklistExpanded(false);
                            setIsPreappChecklistExpanded(false);
                            setIsQuotesExpanded(false);
                            setIsCaseNotesExpanded(false);
                            setIsScriptAssistExpanded(false);
                            setIsFAQExpanded(false);
                            setIsTipsExpanded(false);
                        }}
                    >
                        <img src="/img/arrow.svg" alt="Fold Ai Assist Panel" />
                    </button>
                )}
            </Box>
        );
    },
);

const FeedbackPanel: React.FC<FeedbackPanelProps> = observer(({item, isInitialResponse = false}) => {
    const classes = useStyles();
    const {assistStore} = RootStore;
    const {handleFeedbackChange} = assistStore;

    return (
        <div className={classes.feedbackContainer} style={isInitialResponse ? {position: 'relative'} : {}}>
            {item.wasHelpful === true ? (
                <ThumbUp className={classes.thumbsIcon} onClick={() => handleFeedbackChange(item.responseId, true)} />
            ) : (
                <ThumbUpOutlined
                    className={classes.thumbsIcon}
                    onClick={() => handleFeedbackChange(item.responseId, true)}
                />
            )}
            {item.wasHelpful === false ? (
                <ThumbDown
                    className={classes.thumbsIcon}
                    onClick={() => handleFeedbackChange(item.responseId, false)}
                />
            ) : (
                <ThumbDownOutlined
                    className={classes.thumbsIcon}
                    onClick={() => handleFeedbackChange(item.responseId, false)}
                />
            )}
        </div>
    );
});

export default AgentAssistPanel;
